import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {DefaultPaginationInfo} from "../../../../../../../../../../core/constants/enums";
import {Col} from "reactstrap";
import {
    DataGridActionTypes,
    dataGridCellTextProps,
    DataGridColumnAlignments,
    DataGridColumnTypes,
    DataGridColumnWidthTypes,
    DataGridExoticColumnFields,
    useDataGridSavedState
} from "../../../../../../../../../../packages/data-grid";
import {FreeShippingFeesManagementProductsDataGridColumnNames} from "../../../../../../../../../../core/constants/table/data-grid-column-keys";
import useIsMounted from "../../../../../../../../../hooks/use-is-mounted";
import PartnerDataGrid from "../../../../../../../../../containers/data-grid";
import {FreeShippingFeesManagementViewDataGridActionNames} from "../../index";
import {OutlinedInput} from "../../../../../../../../../components/base/input/outlined_input";
import IconButton from "../../../../../../../../../components/base/button/icon_button";
import {CircularProgress, Fade, SvgIcon} from "@mui/material";
import {Edit} from "@mui/icons-material";
import classnames from "classnames";
import {createUUId, formatMoney} from "../../../../../../../../../../core/services/utils/utils";
import {ReactComponent as RemoveIcon} from "../../../../../../../../../../assets/images/remove.svg";
import Api from "../../../../../../../../../../core/services/api/api";
import ProductInformationPopover from "../../../../../../../../../components/app-specific/draggable-popover/product-information-popover";
import PartAutocomplete from "../../../../../../../../../components/text-fields/autocomplete/part-autocomplete";


const _initialPaginationInfo = {
    ...DefaultPaginationInfo,
}

const dataGridSaveConfig = {
    name: 'FreeShippingFeesManagementViewSettingsProductsExceptionsProductsDataGrid',
    version: 1,
}

const chunkSize = 100;


const FreeShippingFeesManagementViewSettingsProductsExceptions = ({
                                                                      customerId,
                                                                      customerName,
                                                                      syncWithDetachedCustomers,
                                                                      data: dataProp,
                                                                      refresher,
                                                                  }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const isMounted = useIsMounted();
    /** @type {React.MutableRefObject<DataGridController>}*/
    const dataGridController = useRef();

    const isStaticData = useMemo(() => !!dataProp?.length, [dataProp?.length])

    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    name: DataGridExoticColumnFields.selection,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    resizable: false,
                    visibilityToggleable: false,
                    pinnedToggleable: false,
                    reorderable: false,
                    sortable: false,
                    pinned: false,
                },
                {
                    title: "Part No",
                    name: FreeShippingFeesManagementProductsDataGridColumnNames.partNo,
                    type: DataGridColumnTypes.string,
                    width: {
                        size: 1,
                        type: DataGridColumnWidthTypes.flex,
                    },
                },
                // {
                //     title: "Threshold",
                //     name: FreeShippingFeesManagementProductsDataGridColumnNames.threshold,
                //     type: DataGridColumnTypes.element,
                //     sortable: false,
                //     width: {
                //         size: 1,
                //         type: DataGridColumnWidthTypes.flex,
                //     },
                // },
            ],
        [])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
        sortByObjectMap: {
            property: 'field',
            descending: 'descending',
        },
    })

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ..._initialPaginationInfo,
        pageSize: dataGridPageSize
    } : _initialPaginationInfo, [dataGridPageSize]);

    const [paginationInfo, setPaginationInfo] = useState(initialPaginationInfo);
    const [orderBy, setOrderBy] = useState(dataGridSortBy);

    /**
     * Searches through the entries of the free-shipping-fees management view from the server
     * @type {(function(*=, boolean=): Promise<*|[]>)|*}
     */
    const search = useCallback(async (_paginationInfo = undefined, setState = true) => {
        if (setState && isStaticData) {
            setData(dataProp);
            return dataProp;
        }
        const __paginationInfo = {
            pageSize: _paginationInfo?.pageSize ?? paginationInfo?.pageSize,
            currentPage: _paginationInfo?.currentPage ?? paginationInfo?.currentPage,
        };
        const forApi = {
            pagination: __paginationInfo,
            sorts: [],
            customerOriginalId: customerId,
        }
        if (orderBy?.field) {
            forApi.sorts.push({
                property: orderBy.field,
                isDesc: orderBy.descending,
            })
        }
        if (setState)
            setLoading(true);
        const response = await Api.getFreightPartExceptions(forApi);
        if (setState)
            setLoading(false);
        if (response?.resultFlag) {
            if (setState) {
                setData(response.data.items ?? []);
                setPaginationInfo(prevState => ({...prevState, length: response.data?.totalCount}))
            }
            return response.data?.items ?? [];
        }
        return [];
    }, [isStaticData, paginationInfo?.pageSize, paginationInfo?.currentPage, customerId, orderBy?.field, orderBy?.descending, dataProp]);

    /**
     * With each change in the [search] callback:
     * - searches through the entries of the free-shipping-fees management view from the server
     */
    useEffect(() => {
        search().then();
    }, [search, refresher])

    /**
     * Adds a new entity to the list of records for this free-shipping-management view
     */
    const add = useCallback(async (entry) => {
        setAdding(true);
        const partInfoResponse = await Api.getPartInfoWithPartNumber({
            partNumbers: [entry.partNo],
        })
        if (!isMounted() || !partInfoResponse?.resultFlag)
            return;
        const partData = partInfoResponse?.data?.[0];
        const forApi = {
            partNumber: partData?.partNo,
            partId: partData?.id,
            parentCategoryId: partData?.parentCategoryId,
            parentCategoryTitle: partData?.parentCategoryTitle,
            customerOriginalId: customerId,
            customerTitle: customerName,
            applyToAll: syncWithDetachedCustomers,
        }
        const response = await Api.saveFreightPartException(forApi);
        if (!isMounted())
            return;
        setAdding(false);
        if (response?.resultFlag) {
            setData(prevState => [{
                id: createUUId(),
                originalPartId: forApi.partId,
                originalPartNumber: forApi.partNumber,
                threshold: null
            }, ...prevState]);
            setPaginationInfo(prevState => ({...prevState, length: prevState.length + 1}))
        }
    }, [customerId, customerName, isMounted, syncWithDetachedCustomers])

    /**
     * Calls the api regarding removing the list of data
     * @param partIds list of partNos associated to entries to be removed
     * @private
     */
    const remove = useCallback(async (partIds) => {
        setLoading(true);
        const forApi = {
            partOriginalIds: partIds,
            customerOriginalId: customerId,
            applyToAll: syncWithDetachedCustomers,
        }
        const response = await Api.removeFreightPartException(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.filter(e => !partIds.includes(e.originalPartId)))
            setPaginationInfo(prevState => ({...prevState, length: prevState.length - partIds.length}))
        }
        dataGridController.current?.resetSelection();
        setLoading(false);
    }, [isMounted, customerId, syncWithDetachedCustomers])

    /**
     * Updates the threshold of the entity for the fee-shipping-fees management view.
     * @type {function(*, null=): Promise<*>}
     */
    const edit = useCallback(async (entry, threshold = null) => {
        setData(prevState => prevState.map(e => e.id === entry.id ? {...e, editing: true} : e))
        const partInfoResponse = await Api.getPartInfoWithPartNumber({
            partNumbers: [entry.partNumber],
        })
        if (!isMounted() || !partInfoResponse?.resultFlag)
            return;
        const partData = partInfoResponse?.data?.[0];
        const forApi = {
            partNumber: partData?.partNo,
            partId: partData?.id,
            parentCategoryId: partData?.parentCategoryId,
            parentCategoryTitle: partData?.parentCategoryTitle,
            customerOriginalId: customerId,
            customerTitle: customerName,
            applyToAll: syncWithDetachedCustomers,
            threshold: threshold,
        }
        const response = await Api.saveFreightPartException(forApi);
        if (!isMounted())
            return;
        setData(prevState => prevState.map(e => e.id === entry.id ? {...e, editing: false} : e))
        if (response?.resultFlag) {
            setData(prevState => {
                const newState = Array.from(prevState)
                newState.splice(
                    prevState.findIndex(e => e.id === entry.id),
                    1,
                    {...response.data, threshold: forApi.threshold}
                );
                return newState;
            })
        }
        return response?.resultFlag ?? false;
    }, [customerId, customerName, isMounted, syncWithDetachedCustomers])


    /**
     * Sorts the entries of the credits in place.
     * @param {DataGridSortBy} tableOrderBy
     */
    const onSort = useCallback((tableOrderBy) => {
        setOrderBy(tableOrderBy);
        setPaginationInfo(prevState => ({...prevState, currentPage: 1}));
    }, [])

    /**
     * Opens the confirmation dialog regarding this action
     * If the process was confirmed, calls the remove
     * @type {function(Array<any>)}
     */
    const getRemoveConfirmation = useCallback((itemsToBeRemoved) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {`Are you sure that you want to remove the selected item${itemsToBeRemoved?.length > 1 ? '(s)' : ''}?`}
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && remove(itemsToBeRemoved),
        );
    }, [remove])

    /**
     * Fetches all the entries given the available length.
     * @param pageLength
     * @return {Promise<*[]>}
     */
    const getAll = useCallback(async (pageLength) => {
        const _paginationInfo = {
            length: pageLength,
            pageSize: chunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (pageLength > entries.length) {
            const items = await search(_paginationInfo, false);
            if (!items?.length) {
                break;
            }
            entries.push(...items);
            if (!isMounted())
                return entries;
            _paginationInfo.currentPage++;
        }
        return entries;
    }, [isMounted, search])

    /**
     * Handles the action over the selected row in the table based on the name of the action
     * @param action
     * @param selectedRows
     * @param excludedRows
     * @param allRowsSelected
     * @param pagination
     * @return {Promise<void>}
     */
    const onActionInvoked = useCallback(async (action, selectedRows, excludedRows, allRowsSelected, pagination) => {
        switch (action.name) {
            case FreeShippingFeesManagementViewDataGridActionNames.Delete: {
                if (!allRowsSelected) {
                    // not all rows are selected, therefore, the selectedRows list is not empty
                    return getRemoveConfirmation(selectedRows.map(e => e.data.originalPartId));
                }
                if (pagination.pageSize >= pagination.length) {
                    // all the items are within a single page, so we have the data in hand
                    return getRemoveConfirmation(data.map(e => e.originalPartId));
                }
                getRemoveConfirmation((await getAll(pagination.length)).map(e => e.originalPartId));
                break;
            }
            default:
                break;
        }
    }, [data, getAll, getRemoveConfirmation])

    /**
     * Commits the edited value of the provided cell belonging to the [rowKey] and [colName].
     *
     * @param prevValue
     * @param newValue
     * @param {number | string} rowKey
     * @param {number} colName
     * @return {Promise<undefined|boolean>}
     */
    const onCellEdited = useCallback(async (prevValue, newValue, rowKey, colName) => {
        switch (colName) {
            case FreeShippingFeesManagementProductsDataGridColumnNames.threshold:
                let threshold = Number(newValue)
                if (Number.isNaN(threshold))
                    threshold = null;
                if (threshold === 0)
                    threshold = null;
                return edit(data?.find(e => e.id === rowKey), threshold);
            default:
                return;
        }
    }, [data, edit])

    /**@type {DataGridRow[]}}*/
    const dataGridRows = useMemo(() => {
        return data.map(entry => {
            const thresholdRemovable = entry?.threshold !== null;
            return ({
                key: entry.id,
                data: entry,
                cells: {
                    [DataGridExoticColumnFields.selection]: null,
                    [FreeShippingFeesManagementProductsDataGridColumnNames.partNo]:
                        <ProductInformationPopover partNo={entry?.originalPartNumber}>
                            {entry?.originalPartNumber ?? ''}
                        </ProductInformationPopover>,
                    [FreeShippingFeesManagementProductsDataGridColumnNames.threshold]: {
                        value: ({openEditPopover}) =>
                            <>
                                <Fade in={thresholdRemovable}>
                                        <span>
                                        <IconButton
                                            onPressed={() => edit(entry.partNo)}
                                            disabled={entry?.editing || !thresholdRemovable}
                                            color={'paper.error'}
                                        >
                                            {
                                                entry?.editing
                                                    ? <CircularProgress size={20} color={'secondary'}/>
                                                    : <SvgIcon component={RemoveIcon} className='icon small'/>
                                            }
                                        </IconButton>
                                        </span>
                                </Fade>
                                <IconButton
                                    className={classnames({'ml-2': thresholdRemovable})}
                                    onPressed={() => openEditPopover()}
                                    disabled={entry?.editing}
                                >
                                    {
                                        entry?.editing
                                            ? <CircularProgress size={20} color={'secondary'}/>
                                            : <Edit className={"icon small"}/>
                                    }
                                </IconButton>
                                <p {...dataGridCellTextProps} className={classnames('ml-2')}>
                                    {entry.threshold === null ? 'Not Eligible at all' : formatMoney(entry.threshold)}
                                </p>
                            </>,
                        editable: true,
                        editableValue: entry.threshold,
                        EditableContentComponent: <ThresholdEditor/>,
                    },
                },
            })
        })
    }, [data, edit])

    /**
     * Memo version of data grid footer actions
     * @type {DataGridFooterAction[]}
     */
    const footerActions = useMemo(() =>
        /** @type {DataGridFooterAction[]}*/
        [
            {
                name: FreeShippingFeesManagementViewDataGridActionNames.Delete,
                title: 'Remove',
                type: DataGridActionTypes.delete,
                visibilityConstraints: {
                    minSelectedRows: 1,
                },
            },
        ], [])


    return (
        <>
            <div className={'section'}>
                <p className={'section-title'}>
                    Excluded Products
                </p>
                <p className={'section-description'}>
                    Orders containing The following products are excluded in free-shipping calculations.
                </p>
                <div className={'section-paper'}>
                    <div className={'d-flex flex-wrap w-100'}>
                        {
                            !isStaticData &&
                            <Col xs={12}>
                                <PartAutocomplete
                                    clearOnBlur
                                    disabled={adding || loading}
                                    label={"Add Product"}
                                    value={null}
                                    onChange={(_, value) => value && add(value)}
                                />
                            </Col>
                        }
                        <PartnerDataGrid
                            ref={dataGridController}
                            {...dataGridSaveConfig}
                            visibleRows={5}
                            initialLayoutState={{
                                columns: columns,
                            }}
                            hideToolbar
                            hideSelectAll
                            columns={dataGridColumns}
                            rows={dataGridRows}
                            footerActions={footerActions}
                            onActionInvoked={onActionInvoked}
                            onCellEdited={onCellEdited}
                            hideFooter={isStaticData}
                            state={{
                                ...(!isStaticData ? {pagination: paginationInfo, sortBy: orderBy} : {}),
                                loading: {state: loading},
                                density: dataGridDensity,
                            }}
                            onSortByChanged={(_, current) => onSort(current)}
                            onCurrentPageChanged={(_, current) => setPaginationInfo(current)}
                            onPageSizeChanged={(_, current) => setPaginationInfo(current)}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

const ThresholdEditor = ({value, setValue, setCanSubmitChanges}) => {

    /**
     * With each change in the value of the editor:
     * - Determines whether the new value is can be submitted.
     */
    useLayoutEffect(() => {
        const parsed = Number(value)
        setCanSubmitChanges(!Number.isNaN(parsed));
    }, [value])

    /**
     * Changes the value of this editor.
     *
     * - ensures the value is non-negative.
     * @param value
     */
    const onValueChanged = (value) => {
        const parsed = Number(value)
        if (!value || Number.isNaN(parsed))
            return setValue(undefined);
        setValue(Math.max(0, parsed));
    }

    return (
        <>
            <OutlinedInput
                fullWidth
                label={"Threshold"}
                size={'small'}
                value={value?.toString() ?? ''}
                onChange={onValueChanged}
                type={'number'}
                inputProps={{
                    min: 0,
                }}
            />
        </>
    );
}

export default FreeShippingFeesManagementViewSettingsProductsExceptions;
